import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Page, PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate } from "@/lib/labels";
import { toast } from "sonner";
import { CalendarClock, ExternalLink, AlertCircle, RefreshCw, Mail } from "lucide-react";

const ENTITY_TYPES = [
  { code: "AS", label: "Aksjeselskap (AS)" },
  { code: "ENK", label: "Enkeltpersonforetak (ENK)" },
  { code: "ANS", label: "Ansvarlig selskap (ANS)" },
  { code: "DA", label: "Selskap med delt ansvar (DA)" },
  { code: "NUF", label: "Norskregistrert utenlandsk foretak (NUF)" },
];

const CAT_LABELS = {
  registration: "Registrering", tax: "Skatt", reporting: "Rapportering",
  insurance: "Forsikring", accounting: "Regnskap",
};
const FREQ_LABELS = {
  annual: "Årlig", monthly: "Månedlig", quarterly: "Kvartalsvis", "one-time": "Engangs",
};
const REQ = {
  always: { label: "Alltid", cls: "bg-red-500/15 text-red-400" },
  conditionally: { label: "Betinget", cls: "bg-amber-500/15 text-amber-400" },
  never: { label: "Nei", cls: "bg-zinc-500/15 text-zinc-400" },
};

function monthKey(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("nb-NO", { month: "long", year: "numeric" });
}

export default function Compliance() {
  const [entity, setEntity] = useState("AS");
  const [deadlines, setDeadlines] = useState([]);
  const [obligations, setObligations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [sending, setSending] = useState(false);
  const year = new Date().getFullYear();

  const syncTasks = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/compliance/sync-tasks");
      toast.success(data.created ? `${data.created} frist-oppgave(r) opprettet` : "Ingen nye oppgaver – alt er à jour");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSyncing(false); }
  };

  const sendSummaries = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/compliance/send-summaries");
      toast.success(data.sent ? `Fristoppsummering sendt til ${data.sent} ansvarlig(e)` : "Ingen frister å oppsummere");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/compliance/deadlines?year=${year}&entity_type=${entity}`).then((r) => setDeadlines(r.data.deadlines || [])).catch(() => setDeadlines([])),
      api.get(`/compliance/obligations?entity_type=${entity}`).then((r) => setObligations(r.data.obligations || [])).catch(() => setObligations([])),
    ]).finally(() => setLoading(false));
  }, [entity, year]);

  const now = new Date().toISOString();
  const upcoming = deadlines.filter((d) => (d.deadline || "") >= now).sort((a, b) => (a.deadline || "").localeCompare(b.deadline || ""));
  const grouped = upcoming.reduce((acc, d) => { const k = monthKey(d.deadline); (acc[k] = acc[k] || []).push(d); return acc; }, {});

  return (
    <Page>
      <PageHeader title="Compliance & Altinn" subtitle="Frister og plikter fra apier.no (norsk myndighetsdata)" testid="compliance-header">
        <div className="flex items-center gap-2">
          <Button variant="secondary" data-testid="compliance-sync-btn" onClick={syncTasks} disabled={syncing}>
            <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Synkroniserer…" : "Opprett frist-oppgaver"}
          </Button>
          <Button variant="secondary" data-testid="compliance-summary-btn" onClick={sendSummaries} disabled={sending}>
            <Mail className={`w-4 h-4 mr-1 ${sending ? "animate-pulse" : ""}`} /> {sending ? "Sender…" : "Send fristoppsummering"}
          </Button>
          <Select value={entity} onValueChange={setEntity}>
            <SelectTrigger data-testid="compliance-entity-select" className="w-64 bg-secondary border-border"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover border-border">
              {ENTITY_TYPES.map((e) => <SelectItem key={e.code} value={e.code}>{e.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </PageHeader>

      {loading ? <p className="text-muted-foreground">Laster…</p> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-card border border-border rounded-xl p-6" data-testid="compliance-deadlines">
            <div className="flex items-center gap-2 mb-4"><CalendarClock className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} /><h3 className="font-display text-lg">Kommende frister {year}</h3></div>
            {upcoming.length === 0 ? <p className="text-sm text-muted-foreground flex items-center gap-2"><AlertCircle className="w-4 h-4" /> Ingen gjenstående frister for i år.</p> : (
              <div className="space-y-5">
                {Object.entries(grouped).map(([month, items]) => (
                  <div key={month}>
                    <p className="label-caps mb-2 capitalize">{month}</p>
                    <div className="space-y-2">
                      {items.map((d) => (
                        <div key={d.obligation_id} className="flex items-start justify-between gap-3 rounded-lg border border-border p-3" data-testid={`deadline-${d.obligation_id}`}>
                          <div>
                            <p className="text-sm font-medium">{prettyName(d.obligation_name)}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{d.period} · {d.legal_reference}</p>
                          </div>
                          <span className="text-sm whitespace-nowrap">{fmtDate(d.deadline)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-card border border-border rounded-xl p-6" data-testid="compliance-obligations">
            <h3 className="font-display text-lg mb-4">Plikter for selskapsformen</h3>
            <div className="space-y-2">
              {obligations.map((o) => (
                <div key={o.obligation_id} className="rounded-lg border border-border p-3" data-testid={`obligation-${o.obligation_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium">{prettyName(o.obligation_name)}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {CAT_LABELS[o.category] || o.category} · {FREQ_LABELS[o.frequency] || o.frequency}
                        {o.legal_reference ? ` · ${o.legal_reference}` : ""}
                      </p>
                      {o.condition && <p className="text-xs text-muted-foreground mt-1">{o.condition}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs px-2 py-0.5 rounded ${(REQ[o.required] || REQ.never).cls}`}>{(REQ[o.required] || REQ.never).label}</span>
                      {o.source_url && <a href={o.source_url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground"><ExternalLink className="w-3.5 h-3.5" /></a>}
                    </div>
                  </div>
                </div>
              ))}
              {obligations.length === 0 && <p className="text-sm text-muted-foreground">Ingen plikter funnet.</p>}
            </div>
          </div>
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-6">Kilde: apier.no (Brønnøysund Enhetsregisteret + Apier Universal Rulebook). Data er veiledende — kontroller mot offisielle kilder før innsending.</p>
    </Page>
  );
}

function prettyName(n) {
  const map = {
    a_melding: "A-melding", mva_filing: "MVA-melding", naeringsoppgave_as: "Næringsoppgave",
    mva_rate_food: "MVA-sats mat", mva_rate_reduced: "MVA-sats redusert", mva_rate_standard: "MVA-sats standard",
    foretaksregister_as: "Registrering i Foretaksregisteret",
  };
  return map[n] || n;
}
